import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { TychiLogo } from "./TychiLogo";
import { Menu, X } from "lucide-react";
import { useScrollReveal } from "./useAnimations";
import "./website.css";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/platform", label: "Platform" },
  { to: "/modules", label: "Modules" },
  { to: "/technology", label: "Technology" },
  { to: "/security", label: "Security" },
  { to: "/resources", label: "Resources" },
  { to: "/about", label: "About" },
];

function WebsiteNav() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); window.scrollTo(0, 0); }, [location]);

  return (
    <nav className={`w-nav ${scrolled ? "scrolled" : ""}`} data-testid="website-nav">
      <Link to="/" style={{ display: "flex", alignItems: "center" }}>
        <TychiLogo variant={scrolled ? "light" : "dark"} size={120} />
      </Link>
      <div className={`w-nav-center ${menuOpen ? "open" : ""}`}>
        {NAV_LINKS.map(l => (
          <Link key={l.to} to={l.to}
            className={`w-nav-link ${location.pathname === l.to ? "active" : ""}`}>
            {l.label}
          </Link>
        ))}
      </div>
      <div className="w-nav-actions">
        <Link to="/login" className="w-nav-login" data-testid="nav-login-btn">Client Login</Link>
        <Link to="/contact" className="w-nav-cta w-btn" data-testid="nav-contact-btn">Get in Touch</Link>
      </div>
      <button className="w-nav-mobile-btn" onClick={() => setMenuOpen(!menuOpen)}
        data-testid="mobile-menu-toggle" aria-label="Toggle menu">
        {menuOpen ? <X size={24} /> : <Menu size={24} />}
      </button>
    </nav>
  );
}

function WebsiteFooter() {
  return (
    <footer className="w-footer" data-testid="website-footer">
      <div className="w-footer-grid">
        <div className="w-footer-brand">
          <div className="w-footer-brand-row">
            <TychiLogo variant="dark" size={110} />
            <p>AI-enabled institutional financial infrastructure powering managed fund administration services.</p>
          </div>
        </div>
        <div>
          <h4>Platform</h4>
          <Link to="/platform">Overview</Link>
          <Link to="/modules">Modules</Link>
          <Link to="/technology">Technology</Link>
          <Link to="/security">Security</Link>
        </div>
        <div>
          <h4>Company</h4>
          <Link to="/about">About</Link>
          <Link to="/resources">Resources</Link>
          <Link to="/contact">Contact</Link>
          <Link to="/login">Client Login</Link>
        </div>
        <div>
          <h4>Legal</h4>
          <Link to="/privacy-policy">Privacy Policy</Link>
          <Link to="/terms-of-use">Terms of Use</Link>
        </div>
      </div>
      <div className="w-footer-bottom">
        <span>&copy; {new Date().getFullYear()} Tychi. All Rights Reserved.</span>
        <span>Institutional Fund Administration</span>
      </div>
    </footer>
  );
}

export default function WebsiteLayout({ children }) {
  useScrollReveal();
  return (
    <div className="website-page">
      <WebsiteNav />
      <main>{children}</main>
      <WebsiteFooter />
    </div>
  );
}
